import { Auth, Customer, FruitType, Purchase } from './types';
import { CONFIG } from './config-global';
import { authStorage } from './auth';

const API_BASE = CONFIG.serverUrl + '/api';

function getAuthHeaders(): HeadersInit {
  const token = authStorage.getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function handleJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Nếu token hết hạn (401 Unauthorized), xóa auth và reload trang
    if (response.status === 401) {
      authStorage.clearAuth();
      window.location.reload();
      throw new Error('Token đã hết hạn. Vui lòng đăng nhập lại.');
    }
    
    const text = await response.text();
    throw new Error(text || response.statusText);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export interface PurchasePayload {
  customerId: string;
  date: string;
  notes?: string;
  items: Array<{
    fruitTypeId: string;
    fruitTypeName: string;
    price: number;
    weight: number;
    total: number;
  }>;
}

type CustomerPayload = Omit<Customer, 'id' | 'items' | 'totalAmount'>;

export const api = {
  async login(username: string, password: string): Promise<Auth> {
    const res = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    return handleJson<Auth>(res);
  },

  async getFruitTypes(): Promise<FruitType[]> {
    const res = await fetch(`${API_BASE}/fruit-types`, {
      headers: getAuthHeaders(),
    });
    return handleJson<FruitType[]>(res);
  },

  async upsertFruitType(fruit: FruitType, exists: boolean): Promise<FruitType> {
    const url = exists ? `${API_BASE}/fruit-types/${fruit.id}` : `${API_BASE}/fruit-types`;
    const method = exists ? 'PUT' : 'POST';
    const res = await fetch(url, {
      method,
      headers: getAuthHeaders(),
      body: JSON.stringify(fruit),
    });

    if (!res.ok && exists && res.status === 404) {
      return this.upsertFruitType(fruit, false);
    }

    return exists ? fruit : handleJson<FruitType>(res);
  },

  async getCustomers(): Promise<Customer[]> {
    const res = await fetch(`${API_BASE}/customers`, {
      headers: getAuthHeaders(),
    });
    return handleJson<Customer[]>(res);
  },

  async getCustomerByPhone(phone: string): Promise<Customer | null> {
    const res = await fetch(`${API_BASE}/customer?phone=${encodeURIComponent(phone)}`, {
      headers: getAuthHeaders(),
    });
    if (res.status === 404) {
      return null;
    }
    return handleJson<Customer>(res);
  },

  async createCustomer(payload: CustomerPayload): Promise<Customer> {
    const res = await fetch(`${API_BASE}/customers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleJson<Customer>(res);
  },

  async updateCustomer(id: string, payload: CustomerPayload): Promise<void> {
    const res = await fetch(`${API_BASE}/customers/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    await handleJson<void>(res);
  },

  async deleteCustomer(id: string): Promise<void> {
    const res = await fetch(`${API_BASE}/customers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleJson<void>(res);
  },

  async getPurchases(date?: string): Promise<Purchase[]> {
    const url = date ? `${API_BASE}/purchases?date=${encodeURIComponent(date)}` : `${API_BASE}/purchases`;
    const res = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleJson<Purchase[]>(res);
  },

  async createPurchase(payload: PurchasePayload): Promise<Purchase> {
    const res = await fetch(`${API_BASE}/purchases`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleJson<Purchase>(res);
  },

  async updatePurchase(id: string, payload: PurchasePayload): Promise<void> {
    const res = await fetch(`${API_BASE}/purchases/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    await handleJson<void>(res);
  },

  async deletePurchase(id: string): Promise<void> {
    const res = await fetch(`${API_BASE}/purchases/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    await handleJson<void>(res);
  },

  async changePurchaseCustomer(purchaseId: string, newCustomerId: string): Promise<void> {
    const res = await fetch(`${API_BASE}/purchases/${purchaseId}/change-customer`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ newCustomerId }),
    });
    await handleJson<void>(res);
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const res = await fetch(`${API_BASE}/users/change-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    return handleJson<{ message: string }>(res);
  },
};
